/**
 * ¿Comparte esta empresa sus costes con el perito? — D4 = A (Pablo, 26-09-2026).
 *
 * Por defecto NO: un asesor externo no necesita saber a cuánto compra cada
 * cliente, a quién ni con qué facturas (auditoría A6 · T09-01). El admin puede
 * encenderlo en Ajustes → Empresa (`companies.share_costs_with_perito`). La base
 * aplica la misma regla (`perito_hides_costs()`, migración 20260926170200).
 *
 * Una consulta por empresa y sesión: lo leen muchas pantallas a la vez. Si falla
 * (sin red, o la columna aún no existe porque la migración no se ha aplicado) se
 * queda en `false`: ante la duda, no se enseñan costes.
 */
import { supabase } from "@/integrations/supabase/client";

const cache = new Map<string, Promise<boolean>>();

export function fetchPeritoCostsShared(companyId: string): Promise<boolean> {
  let p = cache.get(companyId);
  if (!p) {
    p = (async () => {
      try {
        const { data, error } = await supabase
          .from("companies")
          .select("share_costs_with_perito")
          .eq("id", companyId)
          .maybeSingle();
        if (error) return false;
        return (data as { share_costs_with_perito?: boolean | null } | null)?.share_costs_with_perito === true;
      } catch {
        return false;
      }
    })();
    cache.set(companyId, p);
  }
  return p;
}

/** Tras cambiar el interruptor (o en pruebas). */
export function forgetPeritoCostsShared(companyId?: string) {
  if (companyId) cache.delete(companyId);
  else cache.clear();
}

/** Sub-permisos de la ficha de inventario que son COSTES (precio, valor, coste medio, compras). */
export const COST_SUB_PERMISSIONS = ["inventoryNumbers"] as const;

/**
 * Columnas del inventario que pide el portal del perito: todo MENOS precio,
 * costes y proveedor (A6: antes pedía `select *` y el dato viajaba al navegador
 * aunque la lista no lo pintara).
 */
export const PERITO_INVENTORY_COLUMNS = [
  "id", "company_id", "product_name", "product_name_normalized", "variant_label", "category", "product_type",
  "quantity", "unit", "base_unit", "stock_in_base_units", "status", "reorder_level", "max_applications_per_cycle",
  "storage_location", "batch_number", "registration_number", "treatment_id", "phi_days", "phi_note",
  "default_dose_value", "default_dose_unit", "default_dose_is_legacy", "dose_per_m2", "dose_unit",
  "bio_package_content", "packaging_unit_content", "packaging_unit_count", "packaging_content_unit",
  "units_per_package", "archived_at", "created_at", "updated_at",
].join(", ");

/** Lo que el perito NO pide nunca del inventario. */
export const INVENTORY_COST_COLUMNS = ["unit_price", "cost_per_base_unit", "cost_per_m2", "cost_per_ha", "supplier_name", "supplier_contact"] as const;
